import React, { useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from './ConfirmModal';
import { useAuth } from '../myTrack/AuthContext';

/**
 * Trash icon + confirmation for soft-deleting a single timeline event.
 * Shared by the car, driver and owner timelines; each passes its own delete endpoint.
 *
 * deleteUrl  — full URL of the DELETE endpoint for this event
 * eventTitle — shown in the confirmation so the user knows which event is going
 * onDeleted  — called after a successful delete (typically removes the event from the list)
 */
const DeleteTimelineEventButton = ({ deleteUrl, eventTitle, onDeleted, className = '' }) => {
    const { token } = useAuth();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const close = () => {
        if (loading) return;
        setOpen(false);
        setError(null);
    };

    const handleDelete = async () => {
        setLoading(true);
        setError(null);
        try {
            const authToken = token || localStorage.getItem('authToken');
            await axios.delete(deleteUrl, { headers: { Authorization: `Bearer ${authToken}` } });
            setOpen(false);
            if (onDeleted) onDeleted();
        } catch (err) {
            const data = err.response?.data || {};
            setError(data.message || data.error || 'Could not delete the event. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title="Delete event"
                aria-label="Delete event"
                className={`p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors ${className}`}
            >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
            </button>
            <ConfirmModal
                isOpen={open}
                title="Delete timeline event"
                description={`Delete "${eventTitle || 'this event'}" from the timeline? It will no longer appear in the timeline.`}
                onCancel={close}
                onConfirm={handleDelete}
                confirmText="Delete"
                loading={loading}
                error={error}
                variant="danger"
            />
        </>
    );
};

export default DeleteTimelineEventButton;
